import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import { base_colorAlert } from 'ext-ui/style/colors'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionListModel,
  TransactionManagerRejectionReason,
  transactionManagerReviewRejectionReasonToString,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import type { AdditionalFieldsResponse } from 'src/queries/organizations/additionalFields.queries'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { useQueryUser } from 'src/queries/users.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { toDollars } from 'src/utils/currency.utils'

import style from './TaskItem.style'

interface TaskItemProps {
  task: TransactionListModel
  transactionCustomFields?: Array<
    Pick<AdditionalFieldsResponse, 'id' | 'required'>
  >
  areTransactionCustomFieldsLoaded: boolean
}

const TaskItem = ({
  task,
  transactionCustomFields,
  areTransactionCustomFieldsLoaded,
}: TaskItemProps): React.ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const currentUser = useSelector(selectUserListModelFromState)

  // check for required receipts that are missing
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(currentUser?.organizationId),
  )
  const receiptsMissing =
    receiptManagementEnabled &&
    task.attachmentsCount === 0 &&
    task.receiptRequired

  // check for required exp cats that are missing
  const { data: mandatoryExpenseDetails } = useQueryExpenseCategories({
    filters: { active: true, requiredForTransactions: true },
  })
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const expCatsMissing =
    task.isCleared &&
    task.checkMissingRequiredExpenseCategory(
      activeIntegration?.connectedPlatform,
      mandatoryExpenseDetails,
    )

  const mandatoryTransactionCustomFields = useMemo(
    () => transactionCustomFields?.filter((field) => field.required) ?? [],
    [transactionCustomFields],
  )
  const additionalFieldsMissing =
    task.isCleared &&
    areTransactionCustomFieldsLoaded &&
    mandatoryTransactionCustomFields.some((field) => {
      const fieldDetails = Object.values(task?.additionalFields || {})?.find(
        (txnFields) => txnFields.expenseAdditionalFieldId === field.id,
      )?.details
      return fieldDetails === undefined
    })

  const taskIsRejectedForSpenderAction =
    managerReviewRulesEnabled &&
    task.managerReviewStatus === TransactionManagerReviewStatus.Rejected &&
    [
      TransactionManagerRejectionReason.MissingReceipt,
      TransactionManagerRejectionReason.IncorrectReceipt,
      TransactionManagerRejectionReason.MissingExpenseCategories,
      TransactionManagerRejectionReason.IncorrectExpenseCategories,
      TransactionManagerRejectionReason.MissingNote,
    ].includes(
      task?.managerRejectionReason as TransactionManagerRejectionReason,
    )

  const { data: manager } = useQueryUser({
    userId: task?.managerReviewId,
    enabled: taskIsRejectedForSpenderAction,
  })

  const title = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return translate('Action Required: ${rejectionReason}', {
        rejectionReason: translate(
          transactionManagerReviewRejectionReasonToString(
            task.managerRejectionReason,
          ),
        ),
      })
    } else {
      let missingCopy
      if (receiptsMissing) {
        missingCopy = translate('Missing Receipt')
      }
      if (expCatsMissing) {
        missingCopy = translate('Missing Expense Categories')
      }
      if (additionalFieldsMissing) {
        missingCopy = translate('Missing Custom Fields')
      }
      // are 2+ missing?
      if (
        [receiptsMissing, expCatsMissing, additionalFieldsMissing].filter(
          (val) => val,
        ).length >= 2
      ) {
        missingCopy = translate('Missing Requirements')
      }
      return missingCopy ?? translate('Missing Requirements')
    }
  }, [
    task,
    translate,
    receiptsMissing,
    expCatsMissing,
    additionalFieldsMissing,
    taskIsRejectedForSpenderAction,
  ])

  const titleIcon = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return (
        <SvgFlagNew
          fill={base_colorAlert}
          className={'rejectionFlagIcon'}
          width={14}
          height={14}
        />
      )
    } else {
      return undefined
    }
  }, [task, taskIsRejectedForSpenderAction])

  const subcopy = useMemo(() => {
    if (taskIsRejectedForSpenderAction) {
      return translate(
        task.managerRejectionNote
          ? '${manager} has rejected this transaction: "${rejectionNote}"'
          : '${manager} has rejected this transaction.',
        {
          manager: manager?.fullNameOrEmail || '',
          rejectionNote: task.managerRejectionNote || '',
        },
      )
    } else {
      return translate('Transaction for ${amount} from ${merchant}', {
        merchant: task.merchantName?.toUpperCase() || '',
        amount: currencyFormatter({
          amount: toDollars(task.clearingBillingAmountCents || 0),
          currency: task.clearingBillingCurrency,
        }),
      })
    }
  }, [
    task,
    translate,
    currencyFormatter,
    manager,
    taskIsRejectedForSpenderAction,
  ])

  return (
    <div className={cn('TaskItem', task.id)} css={style}>
      <div className={'flex-row align-center gap-1'}>
        {titleIcon}
        <div className={cn('TaskItem-title')}>{title}</div>
      </div>
      <div className={cn('TaskItem-body')}>{subcopy}</div>
    </div>
  )
}

export default TaskItem
